import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';
import { UnitSelectorComponent } from '@shared/components/unit-selector/unit-selector.component';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { PersonService } from '../services/person.service';
import {
  CreatePersonCommunityHistoryRequest,
  PersonCommunityHistoryItem,
  UpdatePersonCommunityHistoryRequest,
} from '../models/person-community-history.model';

export interface CommunityHistoryEditDialogData {
  item?: PersonCommunityHistoryItem;
  mode: 'create' | 'edit';
  initialDate?: string | null;
  lockDate?: boolean;
  minDate?: Date | null;
}

export interface CommunityHistoryEditDialogResult extends CreatePersonCommunityHistoryRequest, UpdatePersonCommunityHistoryRequest {
  organizationUnitName: string;
}

@Component({
  selector: 'app-community-history-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    DatePickerComponent,
    ModalShellComponent,
    UnitSelectorComponent,
  ],
  templateUrl: './community-history-edit-dialog.component.html',
  styleUrl: './community-history-edit-dialog.component.scss',
})
export class CommunityHistoryEditDialogComponent implements OnDestroy {
  readonly isCreateMode: boolean;
  readonly isSyntheticRecord: boolean;
  readonly modalConfig: ModalShellConfig;
  readonly form: FormGroup;
  readonly minDate: any | null;
  readonly maxDate: any;
  allCommunities: OrganizationUnitItem[] = [];
  selectedCommunity: OrganizationUnitItem | null;
  isLoadingCommunities = true;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly cdr: ChangeDetectorRef,
    private readonly personService: PersonService,
    private readonly dialogRef: MatDialogRef<CommunityHistoryEditDialogComponent, CommunityHistoryEditDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: CommunityHistoryEditDialogData,
  ) {
    this.isCreateMode = data.mode === 'create';
    this.isSyntheticRecord = data.item?.id === 0;
    this.modalConfig = {
      title: this.isCreateMode
        ? 'Agregar pase'
        : this.isSyntheticRecord
          ? 'Corregir comunidad actual'
          : 'Corregir historial',
      size: 'sm',
      layout: 'form',
    };
    this.minDate = data.minDate ? this.fromDate(data.minDate) : null;
    this.maxDate = this.dateAdapter.today();
    this.selectedCommunity = data.item
      ? {
          id: data.item.organizationUnitId,
          name: data.item.organizationUnitName,
          levelId: 5,
          levelName: 'Comunidad',
      } as OrganizationUnitItem
      : null;
    this.allCommunities = this.selectedCommunity ? [this.selectedCommunity] : [];

    this.form = this.fb.group({
      organizationUnitId: [data.item?.organizationUnitId ?? null, Validators.required],
      validFrom: [{ value: this.fromIsoDate(data.item?.validFrom ?? data.initialDate), disabled: data.lockDate === true }],
      reason: [data.item?.reason ?? null, Validators.maxLength(300)],
    });

    this.form.get('validFrom')!.setValidators([Validators.required, this.notBeforeMinDateValidator(), this.notAfterTodayValidator()]);
    this.form.get('validFrom')!.updateValueAndValidity();

    this.form.get('organizationUnitId')!.disable({ emitEvent: false });

    this.personService.getAuthorizedUnits()
      .pipe(
        finalize(() => {
          this.isLoadingCommunities = false;
          this.form.get('organizationUnitId')!.enable({ emitEvent: false });
          this.cdr.markForCheck();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(units => {
        const authorizedCommunities = units.filter(unit => unit.levelId === 5);
        this.allCommunities = this.selectedCommunity
          && !authorizedCommunities.some(unit => unit.id === this.selectedCommunity!.id)
          ? [this.selectedCommunity, ...authorizedCommunities]
          : authorizedCommunities;
        const selectedId = this.form.get('organizationUnitId')!.value;
        if (selectedId) {
          this.selectedCommunity = this.allCommunities.find(unit => unit.id === selectedId) ?? this.selectedCommunity;
        }
        this.cdr.markForCheck();
      });
  }

  onCommunitySelected(unitId: string): void {
    this.selectedCommunity = this.allCommunities.find(unit => unit.id === unitId) ?? null;
  }

  onConfirm(): void {
    const organizationUnitId = this.form.get('organizationUnitId')!.value;
    const selectedCommunity = this.selectedCommunity ?? this.allCommunities.find(unit => unit.id === organizationUnitId) ?? null;

    if (this.form.invalid || !selectedCommunity?.id) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      organizationUnitId: selectedCommunity.id,
      organizationUnitName: selectedCommunity.name,
      validFrom: this.toIsoDate(this.form.get('validFrom')!.value),
      reason: this.form.get('reason')!.value || null,
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private fromIsoDate(value?: string | null): any {
    if (!value) {
      return this.dateAdapter.today();
    }

    const [year, month, day] = value.split('-').map(Number);
    return this.dateAdapter.createDate(year, month - 1, day);
  }

  private fromDate(value: Date): any {
    return this.dateAdapter.createDate(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private notBeforeMinDateValidator(): (control: AbstractControl) => ValidationErrors | null {
    return control => {
      if (!this.minDate || !control.value) {
        return null;
      }

      return this.dateAdapter.compareDate(control.value, this.minDate) < 0 ? { beforeBirthDate: true } : null;
    };
  }

  private notAfterTodayValidator(): (control: AbstractControl) => ValidationErrors | null {
    return control => !control.value || this.dateAdapter.compareDate(control.value, this.maxDate) <= 0
      ? null
      : { futureDate: true };
  }
}
